import { memo, useState, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Eye, Download, Loader, FileX } from 'lucide-react';
import { LISTAR_FACTURAS, OBTENER_FACTURA_COMPLETA } from '../../graphql/factubox';
import { generarFacturaPDF } from '../../utils/generarFacturaPDF';
import Paginacion from './Paginacion';
import './TablaFacturas.css';

/**
 * Tabla de facturas con opciones de ver/descargar PDF
 */
function TablaFacturas({ filtros }) {
  const [paginaActual, setPaginaActual] = useState(1);
  const [generandoPDF, setGenerandoPDF] = useState(null);
  const REGISTROS_POR_PAGINA = 20;

  const { data, loading, error } = useQuery(LISTAR_FACTURAS, {
    variables: {
      fecha_inicio: filtros.fecha_inicio,
      fecha_fin: filtros.fecha_fin,
      busqueda: filtros.busqueda || null,
      limit: REGISTROS_POR_PAGINA,
      offset: (paginaActual - 1) * REGISTROS_POR_PAGINA,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [fetchFacturaCompleta] = useLazyQuery(OBTENER_FACTURA_COMPLETA, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setPaginaActual(1);
  }, [filtros.fecha_inicio, filtros.fecha_fin, filtros.busqueda]);

  const handlePDF = async (facturaId, modo) => {
    setGenerandoPDF(facturaId);
    try {
      const { data: result } = await fetchFacturaCompleta({
        variables: { factura_id: parseInt(facturaId) },
      });
      if (result?.obtenerFacturaCompleta) {
        await generarFacturaPDF(result.obtenerFacturaCompleta, modo);
      }
    } catch (err) {
      console.error('Error al generar PDF:', err);
      alert('Error al generar el PDF');
    } finally {
      setGenerandoPDF(null);
    }
  };

  if (error) {
    return (
      <div className="tabla-factubox">
        <div className="empty-state">
          <FileX size={64} color="#ef4444" />
          <p className="empty-state__title">Error al cargar facturas</p>
          <p className="empty-state__subtitle">{error.message}</p>
        </div>
      </div>
    );
  }

  const facturas = data?.listarFacturas?.facturas || [];
  const total = data?.listarFacturas?.total || 0;
  const totalPaginas = Math.ceil(total / REGISTROS_POR_PAGINA);

  return (
    <div className="tabla-factubox">
      {loading && (
        <div className="loading-overlay">
          <Loader size={40} className="spinner" />
          <p>Cargando facturas...</p>
        </div>
      )}

      {!loading && facturas.length === 0 && (
        <div className="empty-state">
          <FileX size={64} color="#9ca3af" />
          <p className="empty-state__title">No hay facturas</p>
          <p className="empty-state__subtitle">
            Cambia los filtros o genera una nueva factura desde Checkout o POS
          </p>
        </div>
      )}

      {!loading && facturas.length > 0 && (
        <>
          <div className="tabla-wrapper">
            <table className="tabla-facturas">
              <thead>
                <tr>
                  <th className="th-prefijo">PREFIJO</th>
                  <th className="th-numero">NÚMERO</th>
                  <th className="th-cliente">CLIENTE</th>
                  <th className="th-fecha">FECHA</th>
                  <th className="th-tipo">TIPO</th>
                  <th className="th-total">TOTAL</th>
                  <th className="th-acciones">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map((factura) => (
                  <tr key={factura.id}>
                    <td className="td-prefijo">{factura.prefijo || '-'}</td>
                    <td className="td-numero">{factura.numero_factura || '-'}</td>
                    <td className="td-cliente">
                      <div className="cliente-info">
                        <strong>{factura.cliente_nombre || 'Consumidor Final'}</strong>
                        {factura.cliente_documento && (
                          <small>{factura.cliente_documento}</small>
                        )}
                      </div>
                    </td>
                    <td className="td-fecha">{formatFecha(factura.fecha)}</td>
                    <td className="td-tipo">
                      <span className={`badge badge--${getTipoBadge(factura.tipo_factura)}`}>
                        {getTipoLabel(factura.tipo_factura)}
                      </span>
                    </td>
                    <td className="td-total">{formatMoneda(factura.total)}</td>
                    <td className="td-acciones">
                      <div className="acciones-grupo">
                        <button
                          className="btn-accion btn-accion--ver"
                          onClick={() => handlePDF(factura.id, 'ver')}
                          disabled={generandoPDF === factura.id}
                          title="Ver PDF"
                        >
                          {generandoPDF === factura.id ? (
                            <Loader size={16} className="spinner" />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                        <button
                          className="btn-accion btn-accion--pdf"
                          onClick={() => handlePDF(factura.id, 'descargar')}
                          disabled={generandoPDF === factura.id}
                          title="Descargar PDF"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tabla-footer">
            <p className="contador-documentos">
              <strong>{total}</strong> {total === 1 ? 'factura' : 'facturas'}
            </p>
          </div>

          <Paginacion
            paginaActual={paginaActual}
            totalPaginas={totalPaginas}
            totalRegistros={total}
            registrosPorPagina={REGISTROS_POR_PAGINA}
            onCambioPagina={(p) => {
              setPaginaActual(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </>
      )}
    </div>
  );
}

function formatFecha(fecha) {
  if (!fecha) return 'N/A';
  return new Date(fecha).toLocaleDateString('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatMoneda(valor) {
  if (valor === null || valor === undefined) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor);
}

function getTipoBadge(tipo) {
  const map = {
    checkout: 'aceptada',
    venta_pos: 'pendiente',
    evento: 'no-transmitida',
  };
  return map[tipo] || 'no-transmitida';
}

function getTipoLabel(tipo) {
  const map = {
    checkout: 'Hospedaje',
    venta_pos: 'POS',
    evento: 'Evento',
    otro: 'Otro',
  };
  return map[tipo] || tipo || 'Otro';
}

export default memo(TablaFacturas);
